import { ConsentOrdersPathName, Drawer } from "@fla/components";
import { useAppDispatch } from "app/store";
import Logo from "assets/images/logo-1x.webp";
import { TopNavigation } from "components/navigations";
import Hamburger from "hamburger-react";
import { useConsentOrders } from "hooks/ConsentOrders";
import { SliceStatus } from "interfaces";
import { selectConsentOrdersBase, selectInitialDetailsStep } from "pages/ConsentOrders/consentOrdersSelectors";
import { consentOrdersActions } from "pages/ConsentOrders/consentOrdersSlice";
import { consentOrderRoutes } from "pages/ConsentOrders/helper";
import { Suspense, useEffect, useState } from "react";
import { Oval } from "react-loader-spinner";
import { batch, useSelector } from "react-redux";
import { Link, useLocation } from "react-router-dom";

import { LayoutProps } from "../Layout.types";
import { MenuList } from "./MenuList";

export const ConsentOrdersLayout = ({ children, bgColor }: LayoutProps) => {
  const { pathname } = useLocation();
  const [status, setStatus] = useState(SliceStatus.Idle);
  const dispatch = useAppDispatch();

  const [isOpen, setOpen] = useState(false);

  const consentOrderSteps = useSelector(selectInitialDetailsStep);
  const { initialDetails, assetsDebts } = useSelector(selectConsentOrdersBase);

  const { getAssets, getDebts, getConsentOrdersBase } = useConsentOrders();

  const setConsentOrderStep = (targetStep: number) => {
    dispatch(
      consentOrdersActions.setInitialDetailsStep({
        currentStep: targetStep
      })
    );
  };

  const setAssetsDebtsStep = (targetStep: number) => {
    dispatch(
      consentOrdersActions.setAssetsDebtsStep({
        currentStep: targetStep
      })
    );
  };

  const onClose = () => setOpen(false);

  const props = {
    assetsDebts,
    consentOrderSteps,
    setConsentOrderStep,
    setAssetsDebtsStep,
    setOpen
  };

  const coR = consentOrderRoutes.map(r => {
    if (
      r.path === ConsentOrdersPathName.InitialDetails &&
      initialDetails?.children?.if_children?.toLowerCase() === "no"
    ) {
      const subRoutes = Array.isArray(r?.children) ? r.children.filter(s => s.step !== 5) : [];
      return {
        ...r,
        children: subRoutes
      };
    } else return r;
  });

  const fetchData = async () => {
    try {
      setStatus(SliceStatus.Pending);
      const _base = await getConsentOrdersBase();
      if (_base.status) {
        const [_assets, _debts] = await Promise.all([getAssets(), getDebts()]);
        batch(() => {
          dispatch(consentOrdersActions.setAssets(_assets));
          dispatch(consentOrdersActions.setDebts(_debts));
          dispatch(consentOrdersActions.setBase(_base.data!));
        });
      }

      setStatus(SliceStatus.Resolved);
    } catch (error) {
      console.log(error);
      setStatus(SliceStatus.Rejected);
    }
  };

  useEffect(() => {
    (async () => {
      await fetchData();
    })();
  }, []);

  if (status === SliceStatus.Pending) {
    return (
      <article className="min-w-full h-screen flex flex-col justify-center items-center">
        <Oval width={20} height={20} strokeWidth={5} />
      </article>
    );
  } else if (status === SliceStatus.Rejected) {
    return (
      <article className="min-w-full h-screen flex flex-col">
        <p>oops! something went wrong, please try again</p>
      </article>
    );
  }

  if (pathname === "/consent-orders")
    return (
      <main
        className={`min-w-full h-screen ${bgColor} overflow-y-auto`}
        // onScroll={e => setScrollPosition(e.currentTarget.scrollTop)}
      >
        <TopNavigation />
        {children}
      </main>
    );

  return (
    <article className="min-w-full h-screen flex flex-col lg:flex-row">
      <section className="hidden lg:block basis-[250px] shrink-0 max-w-md h-screen overflow-y-auto bg-gray-200 border-r border-gray-300">
        <div className="w-full h-28 flex flex-col justify-center items-center">
          <Link to="/">
            <img src={Logo} className="w-32 h-auto mx-auto" alt="SplitWise Logo" />
          </Link>
        </div>

        <article className="flex flex-col justify-between h-[calc(100%_-_7rem)] w-full overflow-y-auto px-2">
          <ul className="flex flex-col w-full h-auto">
            {coR.map((r, idx) => (
              <MenuList key={+idx} {...r} {...props} />
            ))}
          </ul>
        </article>
      </section>
      {/* Mobile navigation */}

      <div className="bg-gray-200 justify-between items-center px-4 h-16 md:h-20 w-screen sticky top-0 z-999 shadow-md flex lg:hidden">
        <Link to="/">
          <img src={Logo} className="w-16 h-auto my-8" alt="mki-logo" />
        </Link>

        <div className="block lg:hidden">
          <Hamburger direction="right" toggled={isOpen} toggle={setOpen} color="#000" size={25} />
        </div>
      </div>

      {/* for tablet & mobile view */}
      <Drawer show={isOpen} onClose={onClose} classes="xs:px-0 mt-0">
        <ul>
          {consentOrderRoutes.map((d, idx) => (
            <MenuList key={idx} {...d} {...props} />
          ))}
        </ul>

        <div className="px-8 py-5 w-full">{/* <Logout /> */}</div>
      </Drawer>
      <section className="w-full h-screen overflow-y-auto bg-gray-light">
        <Suspense>{children}</Suspense>
      </section>
    </article>
  );
};
